export interface SeasonalDecorPrefs {
  instanceKey: string
  cursor: boolean
  corner: boolean
  logo: boolean
}

export const SEASONAL_DECOR_PREFS_COOKIE_NAME = 'seasonal_decor_prefs'

/** Miroir client : certains navigateurs / hydratation Nuxt ne relisent pas le cookie comme attendu au reload */
const SEASONAL_DECOR_PREFS_STORAGE_KEY = 'monorga.seasonalDecorPrefs'

function readSeasonalDecorPrefsFromLocalStorage(): string | null {
  if (typeof localStorage === 'undefined') {
    return null
  }
  try {
    return localStorage.getItem(SEASONAL_DECOR_PREFS_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeSeasonalDecorPrefsToLocalStorage(rawJson: string): void {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.setItem(SEASONAL_DECOR_PREFS_STORAGE_KEY, rawJson)
  } catch {
    /* quota, mode privé */
  }
}

function readSeasonalDecorPrefsFromDocumentCookie(): string | null {
  if (typeof document === 'undefined') {
    return null
  }
  const prefix = `${SEASONAL_DECOR_PREFS_COOKIE_NAME}=`
  const segments = document.cookie.split(';')
  for (const segment of segments) {
    const part = segment.trim()
    if (!part.startsWith(prefix)) {
      continue
    }
    let value = part.slice(prefix.length)
    try {
      value = decodeURIComponent(value)
    } catch {
      /* valeur déjà lisible */
    }
    return value.length > 0 ? value : null
  }
  return null
}

/**
 * Nuxt `useCookie` peut renvoyer une chaîne ou un objet déjà parsé lorsque le cookie contient du JSON.
 */
export function coerceSeasonalDecorPrefsCookieToRawString(value: unknown): string | null {
  if (value == null) {
    return null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return null
    }
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return null
}

function normalizeSeasonalPrefsCookieRaw(raw: string): string {
  let text = raw.trim()
  if (text.startsWith('"') && text.endsWith('"') && text.length >= 2) {
    try {
      const unwrapped = JSON.parse(text) as unknown
      if (typeof unwrapped === 'string') {
        text = unwrapped
      }
    } catch {
      /* garder tel quel */
    }
  }
  return text
}

/**
 * Ordre : ref useCookie → document.cookie → localStorage (fiabilité au reload).
 */
export function resolveRawSeasonalDecorPrefs(cookieFromComposable: unknown): string | null {
  const fromComposable = coerceSeasonalDecorPrefsCookieToRawString(cookieFromComposable)
  if (fromComposable && fromComposable.trim().length > 0) {
    return normalizeSeasonalPrefsCookieRaw(fromComposable)
  }
  if (import.meta.client) {
    const fromDocument = readSeasonalDecorPrefsFromDocumentCookie()
    if (fromDocument && fromDocument.trim().length > 0) {
      return normalizeSeasonalPrefsCookieRaw(fromDocument)
    }
    const fromStorage = readSeasonalDecorPrefsFromLocalStorage()
    if (fromStorage && fromStorage.trim().length > 0) {
      return normalizeSeasonalPrefsCookieRaw(fromStorage)
    }
  }
  return null
}

export function mirrorSeasonalDecorPrefsToStorage(rawJson: string): void {
  writeSeasonalDecorPrefsToLocalStorage(rawJson)
}

function defaultDecorPrefs(instanceKey: string): SeasonalDecorPrefs {
  return {
    instanceKey,
    cursor: true,
    corner: true,
    logo: true,
  }
}

export function parseDecorPrefsCookie(
  raw: string | null | undefined,
  activeInstanceKey: string,
): SeasonalDecorPrefs {
  if (!raw) {
    return defaultDecorPrefs(activeInstanceKey)
  }
  try {
    const parsed = JSON.parse(raw) as Partial<SeasonalDecorPrefs>
    if (parsed.instanceKey === activeInstanceKey) {
      return {
        instanceKey: activeInstanceKey,
        cursor: parsed.cursor !== false,
        corner: parsed.corner !== false,
        logo: parsed.logo !== false,
      }
    }
  } catch {
    /* ignore */
  }
  return defaultDecorPrefs(activeInstanceKey)
}
